import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../utils/supabaseClient'
import { getOrCreateProfile } from '../utils/auth'
import type { User } from '@supabase/supabase-js'
import type { Profile } from '../utils/supabaseClient'

interface AuthContextType {
  user: User | null
  profile: Profile | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      setUser(session?.user ?? null)
      
      if (session?.user) {
        try {
          const userProfile = await getOrCreateProfile(session.user.id)
          setProfile(userProfile)
        } catch (error) {
          console.error('Error loading profile:', error)
        }
      }
      
      setLoading(false)
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id)
        setUser(session?.user ?? null)
        
        if (session?.user) {
          try {
            const userProfile = await getOrCreateProfile(session.user.id)
            setProfile(userProfile)
          } catch (error) {
            console.error('Error loading profile:', error)
            setProfile(null)
          }
        } else {
          setProfile(null)
        }
        
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const handleSignOut = async () => {
    try {
      console.log('Signing out...')
      setSignOutLoading(true)
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      // Clear user and profile state
      setUser(null)
      setProfile(null)
      setSignOutLoading(false)
      
      console.log('User signed out successfully')
    } catch (error) {
      console.error('Error signing out:', error)
      setSignOutLoading(false)
      throw error
    }
  }

  // State for sign out loading
  const [signOutLoading, setSignOutLoading] = useState(false)

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      signOut: handleSignOut
    }}>
      {children}
    </AuthContext.Provider>
  )
}